import _getNearestColor from "../functions/utility/getNearestColor.js";
import _colorsDict from "../dict/colors.js";
import _ttmlFunc from "../functions/profiles/ttmlGeneral.js";
import { XMLParser as _XMLParser } from "fast-xml-parser";
import _eol from "eol";
import _convertToPlainTextCustom from "../functions/quill/convertToPlainTextCustom.js";
import _convertToPlainText from "../functions/quill/convertToPlainText.js";
import _getFormatOptions from "../functions/helpers/getFormatOptions.js";
import _removeInvalidEvents from "../functions/eventGroups/removeInvalidEvents.js";
import _tcLib from "../lib/timecode.js";
const tcLib = _tcLib;
const removeInvalidEvents = _removeInvalidEvents;
const getFormatOptions = _getFormatOptions;
const convertToPlainText = _convertToPlainText;
const convertToPlainTextCustom = _convertToPlainTextCustom;
const eol = _eol;
const XMLParser = _XMLParser;
const ttmlFunc = _ttmlFunc;
const colorsDict = _colorsDict;
const getNearestColor = _getNearestColor;

/* EBU-TT-D (Tech 3380 v1.0.1): https://tech.ebu.ch/publications/tech3380 */

const STYLE_ATTR_PREFIXES = ["@_tts:", "@_ebutts:", "@_itts:"];
function toArray(value) {
  if (value == null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}
function parseXmlAttrs(attrString) {
  let attrs = {};
  if (!attrString) {
    return attrs;
  }
  let re = /([^\s=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'))?/g;
  let match;
  while (match = re.exec(attrString)) {
    attrs[match[1]] = match[2] !== undefined ? match[2] : match[3] !== undefined ? match[3] : true;
  }
  return attrs;
}
function toHexColor(color, fallback) {
  if (color == null || color === "") {
    return fallback;
  }
  let value = String(color).trim();
  if (value.toLowerCase() === "transparent") {
    return "#00000000";
  }
  if (value.charAt(0) === "#") {
    let hex = value.slice(1);
    if (hex.length === 3 || hex.length === 4) {
      hex = hex.split("").map(ch => ch + ch).join("");
    }
    return "#" + hex.toUpperCase();
  }
  let key = value.toLowerCase();
  return colorsDict.hexColorMapping[key] || colorsDict.hexColorMapping[colorsDict.toBroadcastColor(key)] || fallback || "#FFFFFF";
}
function percentSize(value, fallback) {
  if (value == null || value === "") {
    return fallback;
  }
  let text = String(value).trim();
  if (/%$/.test(text)) {
    return text;
  }
  return `${text}%`;
}
function resolveStyleIds(idRefs, styleById, seen) {
  let merged = {};
  if (!idRefs) {
    return merged;
  }
  seen = seen || new Set();
  String(idRefs).split(/\s+/).filter(Boolean).forEach(id => {
    if (seen.has(id)) {
      return;
    }
    seen.add(id);
    let style = styleById[id];
    if (!style) {
      return;
    }
    if (style["@_style"]) {
      Object.assign(merged, resolveStyleIds(style["@_style"], styleById, seen));
    }
    Object.keys(style).forEach(key => {
      if (key.startsWith("@_") && key !== "@_xml:id" && key !== "@_style") {
        merged[key] = style[key];
      }
    });
  });
  return merged;
}
function styleToInlineAttrs(styleObj) {
  return Object.keys(styleObj).filter(key => STYLE_ATTR_PREFIXES.some(prefix => key.startsWith(prefix))).map(key => `${key.slice(2)}="${styleObj[key]}"`).join(" ");
}
function rewriteReferencedSpanStyles(innerXml, styleById, baseStyle) {
  if (!innerXml) {
    return "";
  }
  return String(innerXml).replace(/<(?:tt:)?span\b([^>]*)>/gi, (full, attrStr) => {
    let attrs = parseXmlAttrs(attrStr);
    let resolved = Object.assign({}, baseStyle || {}, resolveStyleIds(attrs.style, styleById));
    delete attrs.style;
    let kept = Object.keys(attrs).filter(key => ["begin", "end", "xml:id", "xml:space", "xml:lang"].indexOf(key) > -1).map(key => `${key}="${attrs[key]}"`).join(" ");
    let inline = styleToInlineAttrs(resolved);
    let parts = ["span", kept, inline].filter(Boolean).join(" ");
    return `<${parts}>`;
  }).replace(/<\/(?:tt:)?span>/gi, "</span>");
}
function colorTagName(color) {
  if (!color || color === "transparent") {
    return "";
  }
  try {
    let nearest = getNearestColor(color);
    return nearest && nearest.name ? nearest.name : "";
  } catch (err) {
    return "";
  }
}
function wrapFormat(text, styleObj) {
  let output = text;
  if (styleObj["@_tts:fontWeight"] === "bold") {
    output = `<strong>${output}</strong>`;
  }
  if (styleObj["@_tts:fontStyle"] === "italic") {
    output = `<em>${output}</em>`;
  }
  if (styleObj["@_tts:textDecoration"] === "underline") {
    output = `<u>${output}</u>`;
  }
  let fg = colorTagName(styleObj["@_tts:color"]);
  if (fg) {
    output = `<${fg}>${output}</${fg}>`;
  }
  let bg = colorTagName(styleObj["@_tts:backgroundColor"]);
  if (bg) {
    let bgTag = `bg${bg.charAt(0).toUpperCase()}${bg.slice(1)}`;
    output = `<${bgTag}>${output}</${bgTag}>`;
  }
  return output;
}
function styleObjFromInlineAttrs(attrString) {
  let parsed = parseXmlAttrs(attrString);
  let styleObj = {};
  Object.keys(parsed).forEach(key => {
    if (key.indexOf("tts:") === 0 || key.indexOf("ebutts:") === 0 || key.indexOf("itts:") === 0) {
      styleObj["@_" + key] = parsed[key];
    }
  });
  return styleObj;
}
function convertSpansToFormatTags(innerXml) {
  return String(innerXml).replace(/<span\b([^>]*)>([\s\S]*?)<\/span>/gi, (full, attrStr, inner) => {
    let styleObj = styleObjFromInlineAttrs(attrStr);
    return inner.split(/<br\s*\/?>/i).map(part => wrapFormat(part, styleObj)).join("<br/>");
  });
}
function tokenizeParagraphContent(innerXml) {
  let tokens = [];
  let source = String(innerXml || "");
  let re = /<(?:tt:)?span\b([^>]*)>([\s\S]*?)<\/(?:tt:)?span>|<br\s*\/?>/gi;
  let match;
  while (match = re.exec(source)) {
    if (match[0].toLowerCase().indexOf("<br") === 0) {
      tokens.push({
        type: "br",
        raw: "<br/>"
      });
    } else {
      tokens.push({
        type: "span",
        attrs: parseXmlAttrs(match[1]),
        inner: match[2],
        raw: match[0]
      });
    }
  }
  return tokens;
}
function mediaTimeToSec(tc, frameRate, dropFrame, useClockTime) {
  if (!tc) {
    return 0;
  }
  return tcLib.parseTcToSec(tc, frameRate, dropFrame, useClockTime);
}
function isSpanActiveAt(span, atSec, frameRate, dropFrame, useClockTime) {
  if (!span.attrs.begin) {
    return true;
  }
  let start = mediaTimeToSec(span.attrs.begin, frameRate, dropFrame, useClockTime);
  let end = span.attrs.end ? mediaTimeToSec(span.attrs.end, frameRate, dropFrame, useClockTime) : Number.POSITIVE_INFINITY;
  return start <= atSec && end > atSec;
}
function collectParagraphs(body) {
  if (!body) {
    return [];
  }
  let paragraphs = [];
  toArray(body.div).forEach(div => {
    toArray(div && div.p).forEach(p => {
      if (p) {
        paragraphs.push({
          p,
          div
        });
      }
    });
  });
  return paragraphs;
}
function findRegion(regionId, regions, fallbackId) {
  if (regionId) {
    let match = regions.find(region => region["@_xml:id"] === regionId);
    if (match) {
      return match;
    }
  }
  if (fallbackId) {
    return regions.find(region => region["@_xml:id"] === fallbackId) || false;
  }
  return false;
}
function writingModeFromRegion(region) {
  let value = region && (region["@_tts:writingMode"] || region["@_writingMode"]);
  if (!value) {
    return "default";
  }
  if (/^tb/i.test(value)) {
    return "tbrl";
  }
  return "horizontal";
}
function decodeOne(paragraph, region, style, options, useClockTime) {
  let event = ttmlFunc.multiLine.decodeSubtitle(paragraph, region, style, options.frameRate, options.dropFrame, useClockTime, options.window);
  event.writingMode = writingModeFromRegion(region);
  return event;
}
function prepareParagraphText(innerXml, styleById, baseStyle) {
  return convertSpansToFormatTags(rewriteReferencedSpanStyles(innerXml, styleById, baseStyle));
}
function decodeParagraph(paragraph, div, regions, styleById, bodyStyle, options, useClockTime) {
  let regionId = paragraph["@_region"] || div && div["@_region"];
  let region = findRegion(regionId, regions);
  let contentStyle = Object.assign({}, bodyStyle, resolveStyleIds(div && div["@_style"], styleById), resolveStyleIds(paragraph["@_style"], styleById));
  let mergedStyle = Object.assign({}, contentStyle, region ? resolveStyleIds(region["@_style"], styleById) : {});
  let innerXml = paragraph["#text"] != null ? String(paragraph["#text"]) : "";
  let tokens = tokenizeParagraphContent(innerXml);
  let timedSpans = tokens.filter(token => token.type === "span" && token.attrs.begin);
  if (!paragraph["@_begin"] && timedSpans.length) {
    return timedSpans.map(span => {
      let startSec = mediaTimeToSec(span.attrs.begin, options.frameRate, options.dropFrame, useClockTime);
      let activeXml = tokens.map(token => {
        if (token.type === "br") {
          return "<br/>";
        }
        return isSpanActiveAt(token, startSec, options.frameRate, options.dropFrame, useClockTime) ? token.raw : "";
      }).join("");
      let timedParagraph = Object.assign({}, paragraph, {
        "@_begin": span.attrs.begin,
        "@_end": span.attrs.end,
        "#text": prepareParagraphText(activeXml, styleById, contentStyle)
      });
      return decodeOne(timedParagraph, region, mergedStyle, options, useClockTime);
    });
  }
  let prepared = Object.assign({}, paragraph, {
    "#text": prepareParagraphText(innerXml, styleById, contentStyle)
  });
  return [decodeOne(prepared, region, mergedStyle, options, useClockTime)];
}
function styleAttrsToXml(attrs) {
  return Object.keys(attrs).filter(key => attrs[key] != null && attrs[key] !== "").map(key => `${key}="${attrs[key]}"`).join(" ");
}
function ensureStyle(registry, attrs, preferredId) {
  let key = styleAttrsToXml(attrs);
  if (registry.byKey.has(key)) {
    return registry.byKey.get(key);
  }
  let id = preferredId || `style${registry.styles.length + 1}`;
  while (registry.ids.has(id)) {
    id = `${preferredId || "style"}${registry.styles.length + 1}`;
  }
  registry.ids.add(id);
  registry.byKey.set(key, id);
  registry.styles.push({
    id,
    attrs
  });
  return id;
}
function convertInlineTtmlToReferenced(ttmlText, registry, defaultSpanId) {
  let lines = String(ttmlText || "").split(/<br\s*\/?>/i);
  return lines.map(line => {
    let parts = [];
    let re = /<span\s+([^>]*)>([\s\S]*?)<\/span>/gi;
    let lastIndex = 0;
    let match;
    while (match = re.exec(line)) {
      if (match.index > lastIndex) {
        let plain = line.slice(lastIndex, match.index);
        if (plain) {
          parts.push(`<span style="${defaultSpanId}">${plain}</span>`);
        }
      }
      let parsed = parseXmlAttrs(match[1]);
      let spanAttrs = {};
      ["tts:color", "tts:backgroundColor", "tts:fontStyle", "tts:fontWeight", "tts:textDecoration", "tts:fontFamily", "tts:fontSize"].forEach(name => {
        if (parsed[name]) {
          spanAttrs[name] = /color/i.test(name) ? toHexColor(parsed[name], parsed[name]) : parsed[name];
        }
      });
      let inherited = registry.spanDefaults || {};
      let merged = Object.assign({}, inherited, spanAttrs);
      let styleId = ensureStyle(registry, merged);
      parts.push(`<span style="${styleId}">${match[2]}</span>`);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < line.length) {
      let plain = line.slice(lastIndex);
      if (plain) {
        parts.push(`<span style="${defaultSpanId}">${plain}</span>`);
      }
    }
    if (!parts.length) {
      return line ? `<span style="${defaultSpanId}">${line}</span>` : "";
    }
    return parts.join("");
  }).join("<br/>");
}
export default {
  decode: function (input, options) {
    let events = [];
    const xmlToJson = new XMLParser({
      stopNodes: ["*.p"],
      numberParseOptions: {
        skipLike: /[0-9]+/
      },
      ignoreAttributes: false
    });
    let fileJson = xmlToJson.parse(input);
    if (!fileJson.tt) {
      fileJson = {
        tt: fileJson
      };
    }
    if (!fileJson.tt || !fileJson.tt.body) {
      return events;
    }
    let timeBase = ttmlFunc.getTimeBaseValue(input) || "media";
    let useClockTime = timeBase === "media" || timeBase === "clock";
    let regions = toArray(fileJson.tt.head && fileJson.tt.head.layout && fileJson.tt.head.layout.region);
    let styles = toArray(fileJson.tt.head && fileJson.tt.head.styling && fileJson.tt.head.styling.style);
    let styleById = {};
    styles.forEach(style => {
      if (style && style["@_xml:id"]) {
        styleById[style["@_xml:id"]] = style;
      }
    });
    let bodyStyle = resolveStyleIds(fileJson.tt.body["@_style"], styleById);
    collectParagraphs(fileJson.tt.body).forEach(({
      p,
      div
    }) => {
      events.push(...decodeParagraph(p, div, regions, styleById, bodyStyle, options, useClockTime));
    });
    return events;
  },
  encode: function (eventGroup, options) {
    let encodingOptions = getFormatOptions(options.formatOptions);
    let language = encodingOptions["Language Code"] || "en";
    let cellResolution = encodingOptions["Cell Resolution"] || "32 15";
    let fontColor = toHexColor(encodingOptions["Font Color"] || "white", "#FFFFFF");
    let backgroundColor = toHexColor(encodingOptions["Background Color"] || "black", "#000000");
    let fontFamily = encodingOptions["Font Family"] || "monospaceSansSerif";
    let fontSize = percentSize(encodingOptions["Font Size"] || "160", "160%");
    let fontWeight = encodingOptions["Font Weight"] || "normal";
    let fontStyle = encodingOptions["Font Style"] || "normal";
    let registry = {
      styles: [],
      byKey: new Map(),
      ids: new Set(),
      spanDefaults: {
        "tts:color": fontColor,
        "tts:backgroundColor": backgroundColor,
        "tts:fontFamily": fontFamily,
        "tts:fontSize": fontSize
      }
    };
    let defaultStyleId = ensureStyle(registry, {
      "tts:fontFamily": fontFamily,
      "tts:fontSize": fontSize,
      "tts:lineHeight": "normal",
      "tts:color": fontColor,
      "tts:backgroundColor": backgroundColor,
      "tts:fontStyle": fontStyle,
      "tts:fontWeight": fontWeight,
      "tts:textDecoration": "none"
    }, "defaultStyle");
    let spanStyleId = ensureStyle(registry, registry.spanDefaults, "spanStyle");
    let paragraphStyleIds = {
      left: ensureStyle(registry, {
        "tts:textAlign": "left"
      }, "paragraphLeft"),
      center: ensureStyle(registry, {
        "tts:textAlign": "center"
      }, "paragraphCenter"),
      right: ensureStyle(registry, {
        "tts:textAlign": "right"
      }, "paragraphRight")
    };
    const lineExtent = 7.33;
    const rowHeight = 80 / (options.window && options.window.height ? options.window.height : 15);
    let regionMap = new Map();
    let paragraphs = "";
    eventGroup.events.forEach((event, index) => {
      let plainTextCustom = convertToPlainTextCustom(event.text, "\n", false, "strong", "em", "u", true);
      let ttmlText = ttmlFunc.multiLine.convertToTtml(plainTextCustom);
      let referencedText = convertInlineTtmlToReferenced(ttmlText, registry, spanStyleId);
      let plainText = convertToPlainText(event.text);
      let start = tcLib.secToTcMs(event.start, options.frameRate, options.dropFrame).replace(",", ".");
      let end = tcLib.secToTcMs(event.end, options.frameRate, options.dropFrame).replace(",", ".");
      let alignment = ["left", "center", "right"].includes(event.alignment) ? event.alignment : "center";
      let paragraphStyleId = paragraphStyleIds[alignment];
      let lineCount = Math.max(1, eol.split(plainText).length);
      let regionId, regionMarkup;
      if (event.yPos === "start") {
        regionId = "top";
        regionMarkup = `<region xml:id="top" tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="before" tts:writingMode="lrtb"/>`;
      } else if (event.yPos === "center") {
        regionId = "center";
        regionMarkup = `<region xml:id="center" tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="center" tts:writingMode="lrtb"/>`;
      } else if (!isNaN(parseInt(event.yPos, 10))) {
        let startLine = parseInt(event.yPos, 10);
        let endLine = startLine + lineCount - 1;
        let extentHeight = parseFloat((lineCount * lineExtent).toFixed(2));
        let yOrigin = parseFloat((10 + endLine * rowHeight - extentHeight).toFixed(2));
        regionId = lineCount > 1 ? `region.${startLine}_${endLine}` : `region.${startLine}`;
        regionMarkup = `<region xml:id="${regionId}" tts:origin="10% ${yOrigin}%" tts:extent="80% ${extentHeight}%" tts:displayAlign="after" tts:writingMode="lrtb"/>`;
      } else {
        regionId = "bottom";
        regionMarkup = `<region xml:id="bottom" tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="after" tts:writingMode="lrtb"/>`;
      }
      if (event.writingMode === "tbrl") {
        regionId = `${regionId}.tbrl`;
        regionMarkup = regionMarkup.replace(`xml:id="${regionId.replace(".tbrl", "")}"`, `xml:id="${regionId}"`).replace(`tts:writingMode="lrtb"`, `tts:writingMode="tbrl"`);
      }
      if (!regionMap.has(regionId)) {
        regionMap.set(regionId, regionMarkup);
      }
      paragraphs += eol.after(`<p xml:id="sub${index + 1}" style="${paragraphStyleId}" region="${regionId}" begin="${start}" end="${end}">${referencedText}</p>`);
    });
    let styling = "";
    registry.styles.forEach(style => {
      styling += eol.after(`        <style xml:id="${style.id}" ${styleAttrsToXml(style.attrs)}/>`);
    });
    let regions = "";
    if (regionMap.size === 0) {
      regions += eol.after(`<region xml:id="bottom" tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="after" tts:writingMode="lrtb"/>`);
    } else {
      regionMap.forEach(regionMarkup => {
        regions += eol.after(regionMarkup);
      });
    }
    let output = eol.after(`<?xml version="1.0" encoding="UTF-8"?>`);
    output += eol.after(`<tt xmlns="http://www.w3.org/ns/ttml" xmlns:ttp="http://www.w3.org/ns/ttml#parameter" xmlns:tts="http://www.w3.org/ns/ttml#styling" xmlns:ttm="http://www.w3.org/ns/ttml#metadata" xmlns:ebuttm="urn:ebu:tt:metadata" xmlns:ebutts="urn:ebu:tt:style" xmlns:ittp="http://www.w3.org/ns/ttml/profile/imsc1#parameter" xmlns:itts="http://www.w3.org/ns/ttml/profile/imsc1#styling" xml:lang="${language}" ttp:timeBase="media" ttp:cellResolution="${cellResolution}">`);
    output += eol.after(`<head>`);
    output += eol.after(`<metadata>
        <ebuttm:conformsToStandard>urn:ebu:tt:distribution:2018-04</ebuttm:conformsToStandard>
        <ebuttm:conformsToStandard>http://www.w3.org/ns/ttml/profile/imsc1/text</ebuttm:conformsToStandard>
    </metadata>`);
    output += eol.after(`<styling>`);
    output += styling;
    output += eol.after(`    </styling>`);
    output += eol.after(`<layout>`);
    output += regions;
    output += eol.after(`</layout>`);
    output += eol.after(`</head>`);
    output += eol.after(`<body style="${defaultStyleId}">`);
    output += eol.after(`<div>`);
    output += paragraphs;
    output += eol.after(`</div>`);
    output += eol.after(`</body>`);
    output += `</tt>`;
    return output;
  },
  preProcess: {
    encode: function (eventGroup) {
      return removeInvalidEvents(eventGroup);
    },
    decode: function (input) {
      return eol.lf(input);
    }
  },
  postProcess: {
    encode: function (output) {
      return output;
    },
    decode: function (eventGroup) {
      return eventGroup;
    }
  }
};